import * as yup from "yup";
import { v4 } from "uuid";

export const documentTypes = [
  ["PASSPORT", "Passport"],
  ["NATIONAL_IDENTITY_CARD", "National Identity Card"],
  ["LEGAL_PERMANENT_RESIDENT_CARD", "Legal Permanent Resident Card"],
];

export const genderOpts = [
  ["MR", "Mr."],
  ["MS", "Ms."],
];

function commomFields(t) {
  return {
    title: yup
      .string()
      .required(`${t("title")} ${t("isRequired")}`)
      .default(""),
    first_name: yup
      .string()
      .required(`${t("firstName")} ${t("isRequired")}`)
      .test("matches", t("latinCharactersAndSpacesOnly"), function (value) {
        if (!value) return true; // Skip validation if value is empty
        return /^[a-zA-Z ]+$/.test(value);
      })
      .default(""),
    last_name: yup
      .string()
      .required(`${t("lastName")} ${t("isRequired")}`)
      .test("matches", t("latinCharactersAndSpacesOnly"), function (value) {
        if (!value) return true; // Skip validation if value is empty
        return /^[a-zA-Z ]+$/.test(value);
      })
      .default(""),
    age_type: yup.string().oneOf(["ADULT", "YOUTH", "SENIOR"]).default("ADULT"),
    date_of_birth: yup
      .date()
      .typeError(`${t("dateOfBirth")} ${t("isRequired")}`)
      .required(`${t("dateOfBirth")} ${t("isRequired")}`)
      .default(null),
  };
}

function leaderExtraFields(t) {
  return {
    email_address: yup
      .string()
      .email()
      .test("has-top-level-domain", "Invalid email", (value) => {
        if (!value) return true;
        const domain = value.split("@")[1];
        return !!domain && domain.includes(".");
      })
      .when("lead_traveler", {
        is: true,
        then: (schema) => schema.required(`${t("email")} ${t("isRequired")}`),
      })
      .default(""),
    phone_number: yup
      .string()
      .when("lead_traveler", {
        is: true,
        then: (schema) => schema.required(`${t("phone")} ${t("isRequired")}`),
      })
      .test("valid-country-code", "Invalid phone number", (value) => {
        if (!value) return true;
        const countryCodePattern = /^(\+|00)[1-9]\d{0,2}(\s*)/;
        return countryCodePattern.test(value);
      })
      .default(""),
  };
}

function reductionCardId(t) {
  return {
    reduction_card_id: yup
      .string()
      .required(`${t("reductionCardId")} ${t("isRequired")}`)
      .default(""),
  };
}

function countryOfResidence(t) {
  return {
    country_of_residence: yup
      .string()
      .required(`${t("countryOfResidence")} ${t("isRequired")}`)
      .default(""),
  };
}

function place_of_birth(t) {
  // No, it is not wrong, comes as "place_of_birth" from the API but the field name is "country_of_birth"
  return {
    country_of_birth: yup
      .string()
      .required(`${t("countryOfBirth")} ${t("isRequired")}`)
      .default(""),
  };
}

function passport(t) {
  return {
    traveler_document: yup.object().shape({
      country_code: yup
        .string()
        .required(`${t("countryCode")} ${t("isRequired")}`),
      document_number: yup
        .string()
        .required(`${t("documentNumber")} ${t("isRequired")}`),
      expiration_date: yup
        .array()
        .of(yup.date().required(`${t("expirationDate")} ${t("isRequired")}`))
        .required(),
      doc_type: yup
        .string()
        .oneOf(documentTypes.map((doc) => doc[0]))
        .required(`${t("docType")} ${t("isRequired")}`),
    }),
  };
}

export function travellerSchema(t, defaultReqFields) {
  return yup.object({
    lead_traveler: yup.boolean().default(false).required(t("isRequired")),
    ...commomFields(t, defaultReqFields),
    ...leaderExtraFields(t),
    ...(defaultReqFields.includes("reduction_card_id")
      ? reductionCardId(t)
      : []),
    ...(defaultReqFields.includes("country_of_residence")
      ? countryOfResidence(t)
      : []),
    ...(defaultReqFields.includes("country_of_birth") ? place_of_birth(t) : []),
    ...(defaultReqFields.includes("passport") ? passport(t) : []),
    // nationality: yup.string().length(2).required(t("isRequired")).default(null),
    uid: yup.string().default(v4()).required(t("isRequired")),
  });
}

export function validationSchema({ t, defaultReqFields }) {
  return yup.object({
    adults: yup.array().of(travellerSchema(t, defaultReqFields)),
    youths: yup.array().of(travellerSchema(t, defaultReqFields)),
    seniors: yup.array().of(travellerSchema(t, defaultReqFields)),
  });
}

export function toLowerCaseArrayValues(arr) {
  if (!arr) return [];
  return arr.map((field) => field.toLowerCase());
}
